// @ts-nocheck
import React from "react";
import { createRoot } from "react-dom/client";
import {
  init as tgSdkInit,
  restoreInitData as tgRestoreInitData,
  retrieveRawInitData as tgRetrieveRawInitData,
} from "@telegram-apps/sdk";
import { StingScreens, withSting } from "./screens";
import { BOT_USERNAME, TEAM_BOOST_THRESHOLD, playersLabel } from "./promo";
import { TutorialOverlay, markTutorialSeen } from "./tutorial";
import * as Snd from "./sound";
import * as Hx from "./haptics";
import * as J from "./juice";
import { CampaignUnavailable } from "./campaign-unavailable";
import { PrizeBanner, PrizeScreen } from "./prize";
import { OfferGate } from "./offer-gate";
import { closeMiniApp } from "./telegram";
import { createLatestRequestGate } from "./request-gate";
import {
  CLIENT_EVENT_NAME,
  EVENT_RESULT,
  createEventOutbox,
  EventBatchError,
  installEventCapture,
} from "./event-outbox";

const S = StingScreens;
/* Team x2 threshold copy comes from config/promo.config.json via promo.ts. */
const PLAYERS_RU = playersLabel(TEAM_BOOST_THRESHOLD, "ru");
const PLAYERS_UZ = playersLabel(TEAM_BOOST_THRESHOLD, "uz");
const PROD_API_BASE = "https://api.stingpromo.com";
const DEV_API_BASE = "http://localhost:8000";
const DEFAULT_API_BASE = import.meta.env.PROD ? PROD_API_BASE : DEV_API_BASE;
const API_BASE = import.meta.env.VITE_API_BASE_URL || window.__API_BASE__ || DEFAULT_API_BASE;
// Shutdown build (2026-09-20): the backend is gone, so the bundle must not
// call any API at all - render the ended screen straight from the bundle.
const CAMPAIGN_ENDED = import.meta.env.VITE_CAMPAIGN_ENDED === "1";
const SESSION_REQUEST_TIMEOUT_MS = 8_000;
const API_REQUEST_TIMEOUT_MS = 15_000;
const EVENT_REQUEST_TIMEOUT_MS = 8_000;
const authNoticeVisual = (lang) => (
  `/ds/assets/approved-kv/sting-promo-horizontal-${lang === "uz" ? "uz" : "ru"}.webp`
);
const TICKET_HISTORY_LIMIT = 50;
const VIEW_ALIASES = {
  home: "home",
  main: "home",
  team: "team",
  crew: "team",
  cabinet: "cabinet",
  profile: "cabinet",
  lk: "cabinet",
  tickets: "tickets",
  baku: "baku",
  f1: "baku",
  scan: "scan",
  rules: "rules",
  offer: "offer",
  prize: "prize",
  winner: "prize",
};

/* ---------- i18n for notices, errors, info modals and toasts (RU/UZ formal) ---------- */
const MSG = {
  ru: {
    loadAuthTitle: "Откройте через Telegram",
    loadAuthBody: `Приложение работает только внутри Telegram. Откройте его кнопкой в боте ${BOT_USERNAME} и нажмите «Повторить».`,
    loadPauseTitle: "ГОНКА STING на паузе",
    loadPauseBody: "Не получилось соединиться. Проверьте интернет и нажмите «Повторить».",
    errFallback: "Что-то пошло не так. Попробуйте ещё раз.",
    scanReading: ["Читаем чек", "Проверяем в Soliq"],
    retry: "Повторить", ok: "Понятно", close: "Закрыть",
    e: {
      tour_not_active: ["Приём чеков пока закрыт", "Приём чеков откроется 31 июля 2026 года в 12:00 по времени Ташкента."],
      phone_required: ["Нужен подтверждённый телефон", `Вернитесь в бот ${BOT_USERNAME}, отправьте номер телефона и снова откройте приложение.`],
      offer_version_invalid: ["Правила обновились", "Откройте актуальные Правила акции и подтвердите новую версию перед продолжением."],
      auth_restart_required: ["Сессия устарела", `Закройте приложение и откройте его заново кнопкой в боте ${BOT_USERNAME}.`],
      request_failed: ["Не удалось выполнить действие", "Проверьте интернет и попробуйте снова."],
      offer_required: ["Нужно принять Правила", "Откройте Правила акции в личном кабинете и нажмите кнопку принятия."],
      receipt_already_used: ["Чек уже использован", "Этот чек уже посчитан. Отсканируйте другой чек."],
      receipt_daily_limit: ["Лимит чеков на сегодня", "Сегодня уже засчитано 3 чека. Возвращайтесь завтра по времени Ташкента."],
      receipt_outside_campaign: ["Чек вне периода акции", "Этот чек оформлен вне периода акции. Отсканируйте чек, купленный в даты акции."],
      receipt_invalid_qr: ["Нужен QR фискального чека", "Это не похоже на QR фискального чека. Наведите камеру на QR-код внизу чека или вставьте ссылку из чека."],
      receipt_no_sting: ["В чеке нет Sting", "Отсканируйте чек, где есть участвующий в акции Sting."],
      receipt_not_found: ["Чек ещё обрабатывается", "Чек пока не появился в системе Soliq. Подождите минуту и попробуйте снова."],
      receipt_forged: ["Чек не прошёл проверку", "Этот чек не прошёл проверку Soliq. Убедитесь, что сканируете настоящий фискальный чек с QR-кодом."],
      receipt_soliq_unavailable: ["Сервис проверки недоступен", "Сервис проверки чеков Soliq сейчас недоступен. Попробуйте чуть позже."],
      receipt_scan_cooldown: ["Слишком много попыток", "Вы отправили много чеков подряд. Немного подождите и попробуйте снова."],
      receipt_kiz_already_used: ["Товар уже засчитан", "Этот товар из чека уже был использован в акции."],
      receipt_moderation_required: ["Чек не распознан", "Не получилось прочитать данные чека. Проверьте, что это QR с фискального чека, и попробуйте ещё раз."],
      conflict: ["Действие уже выполнено", "Этот запрос уже обработан. Обновите экран и проверьте статус."],
      pitstop_no_attempts: ["Нет попыток для игры", "Купите Sting, попросите на кассе чек с QR-кодом и отсканируйте его. Если окно уже открыто, вы ещё успеете сыграть."],
      pitstop_already_played: ["Мини игра уже пройдена", "Сегодня вы уже сыграли. Попытки сохранятся до следующей игры."],
      team_invite_not_found: ["Приглашение не сработало", "Попросите друга прислать новую ссылку."],
      already_in_team: ["Вы уже в команде", "Сначала выйдите из текущей команды - ваши шансы останутся с вами."],
      team_full: ["Команда заполнена", "В команде уже 20 участников."],
      rate_limited: ["Команда уже вызвана", "Подождите немного и попробуйте снова."],
      pitstop_window_closed: ["Мини-игра ещё не открыта", "Играть можно только во время мини-игры. Время старта - на главном экране."],
      team_call_limit: ["Команда уже позвана", "Позвать команду можно один раз в день."],
      team_call_unavailable: ["Сейчас позвать нельзя", "Позвать команду можно до конца сегодняшней мини-игры."],
      story_sharing_disabled: ["Сториз пока недоступны", "Функция включится после возобновления кампании."],
      story_not_available: ["Карточка пока не готова", "Для этой сториз нужен активный сбор, команда или подтверждённый результат."],
      story_not_found: ["Сториз не найдена", "Создайте новую карточку и попробуйте ещё раз."],
      story_code_expired: ["Срок карточки истёк", "Создайте новую сториз-карточку."],
    },
    toastOffer: ["Правила приняты", "Теперь доступны чеки, команды и мини игры."],
    infoPrizes: ["Призы", "В Туре 1 разыгрываются 4 поездки на двоих. Полные условия приза указаны в Правилах акции. Следите за обновлениями - призовой фонд может быть увеличен и дополнен."],
    infoOffer: ["Правила акции", "Откройте полный текст Правил акции на русском языке и ознакомьтесь с условиями до принятия."],
    offerAccept: "Принять Правила",
    offerOpen: "Открыть полный текст",
    offerUnavailable: "Утверждённый текст Правил акции на русском языке пока недоступен. Принятие условий отключено.",
    infoTeamX2: [
      "Как работает удвоение x2",
      `Удвоение действует только на покупки того же дня. Соберите ${PLAYERS_RU} из команды в один день: каждый играет в мини-игру и сканирует свои чеки в этот же день - тогда шансы с этих чеков удвоятся. Чеки других дней и шансы тех, кто не играл, не удваиваются.`,
    ],
    infoSupport: ["Поддержка", `Горячая линия: +998 71 215-82-85. Если проблема повторяется, напишите в Telegram-бот ${BOT_USERNAME}.`],
    loading: "Загружаем ГОНКУ STING",
    campaignGateTitle: "ГОНКА STING пока закрыта",
    campaignGateBody: "Приём чеков откроется автоматически 31 июля 2026 года в 12:00 по времени Ташкента.",
    campaignEndedTitle: "ГОНКА STING завершена",
    campaignEndedBody: "Спасибо за участие! Следите за новостями Sting.",
    callYes: "Команда вызвана", callNo: "Некого звать",
    callDelivered: (s, t) => `Доставлено: ${s}/${t}.`, callNone: `В команде пока нет участников с открытым ботом ${BOT_USERNAME}.`,
    practiceDone: ["Тренировка завершена", `Очки за тренировку не идут. Настоящая игра открыта до конца дня после доставки приглашения в бот ${BOT_USERNAME}.`],
    teamUpd: "Команда обновлена", teamNow: (n) => `Теперь вы в ${n}.`,
    leftTitle: "Вы вышли из команды", leftBody: "Ваши личные шансы остались с вами.",
    profileTitle: "Профиль сохранён", profileBody: "Имя и команда обновлены.",
  },
  uz: {
    loadAuthTitle: "Telegram orqali oching",
    loadAuthBody: `Ilova faqat Telegram ichida ishlaydi. Uni ${BOT_USERNAME} botidagi tugma orqali oching va «Qayta urinish»ni bosing.`,
    loadPauseTitle: "STING POYGASI pauzada",
    loadPauseBody: "Ulanib boʼlmadi. Internetni tekshiring va «Qayta urinish»ni bosing.",
    errFallback: "Nimadir xato ketdi. Qaytadan urinib koʼring.",
    scanReading: ["Chek oʼqilmoqda", "Soliqda tekshirilyapti"],
    retry: "Qayta urinish", ok: "Tushunarli", close: "Yopish",
    e: {
      tour_not_active: ["Cheklar hozircha qabul qilinmaydi", "Cheklar 2026-yil 31-iyul kuni Toshkent vaqti bilan soat 12:00 dan qabul qilinadi."],
      phone_required: ["Tasdiqlangan telefon kerak", `${BOT_USERNAME} botiga qayting, telefon raqamingizni yuboring va ilovani qayta oching.`],
      offer_version_invalid: ["Qoidalar yangilandi", "Davom etishdan oldin amaldagi Aksiya qoidalarini oching va yangi versiyani tasdiqlang."],
      auth_restart_required: ["Sessiya eskirdi", `Ilovani yoping va ${BOT_USERNAME} botidagi tugma orqali qaytadan oching.`],
      request_failed: ["Amalni bajarib boʼlmadi", "Internetni tekshiring va qayta urinib koʼring."],
      offer_required: ["Qoidalarni qabul qilish kerak", "Aksiya qoidalarini kabinetda oching va qabul qilish tugmasini bosing."],
      receipt_already_used: ["Chek allaqachon ishlatilgan", "Bu chek allaqachon hisoblangan. Boshqa chekni skanlang."],
      receipt_daily_limit: ["Bugungi chek limiti", "Bugun allaqachon 3 ta chek hisobga olindi. Toshkent vaqti boʻyicha ertaga qaytib keling."],
      receipt_outside_campaign: ["Chek aksiya davriga kirmaydi", "Bu chek aksiya muddatidan tashqarida chiqarilgan. Aksiya kunlarida xarid qilingan chekni skanlang."],
      receipt_invalid_qr: ["Fiskal chek QR-kodi kerak", "Bu fiskal chek QR-kodiga oʼxshamaydi. Kamerani chek pastidagi QR-kodga toʼgʼrilang yoki chekdagi havolani joylashtiring."],
      receipt_no_sting: ["Chekda Sting yoʼq", "Aksiyada ishtirok etuvchi Sting bor chekni skanerlang."],
      receipt_not_found: ["Chek hali qayta ishlanmoqda", "Chek hali Soliq tizimida paydo boʼlmadi. Bir daqiqa kutib, qaytadan urinib koʼring."],
      receipt_forged: ["Chek tekshiruvdan oʼtmadi", "Bu chek Soliq tekshiruvidan oʼtmadi. QR-kodli haqiqiy fiskal chekni skanlayotganingizga ishonch hosil qiling."],
      receipt_soliq_unavailable: ["Tekshiruv xizmati ishlamayapti", "Soliq chek tekshiruv xizmati hozir ishlamayapti. Birozdan soʼng urinib koʼring."],
      receipt_scan_cooldown: ["Juda koʼp urinish", "Siz ketma-ket koʼp chek yubordingiz. Biroz kutib, qaytadan urinib koʼring."],
      receipt_kiz_already_used: ["Mahsulot allaqachon hisobga olingan", "Bu chekdagi mahsulot aksiyada allaqachon ishlatilgan."],
      receipt_moderation_required: ["Chek aniqlanmadi", "Chek maʼlumotlarini oʼqib boʼlmadi. Fiskal chekdagi QR ekanini tekshirib, qaytadan urinib koʼring."],
      conflict: ["Amal allaqachon bajarilgan", "Bu soʼrov allaqachon qayta ishlangan. Ekranni yangilab, holatni tekshiring."],
      pitstop_no_attempts: ["Oʼyin uchun urinish yoʼq", "Sting xarid qiling, kassadan QR-kodli chek soʼrang va uni skanerlang. Oyna ochiq boʼlsa, hali oʼynashga ulgurasiz."],
      pitstop_already_played: ["Mini-oʻyin allaqachon oʻtildi", "Bugun allaqachon oʼynadingiz. Urinishlar keyingi oʼyingacha saqlanadi."],
      team_invite_not_found: ["Taklif ishlamadi", "Doʼstingizdan yangi havola yuborishni soʼrang."],
      already_in_team: ["Siz allaqachon jamoadasiz", "Avval joriy jamoadan chiqing - imkoniyatlaringiz sizda qoladi."],
      team_full: ["Jamoa toʼlgan", "Jamoada allaqachon 20 ta ishtirokchi bor."],
      rate_limited: ["Jamoa allaqachon chaqirilgan", "Biroz kutib, qayta urinib koʼring."],
      pitstop_window_closed: ["Mini-oʼyin hali ochilmadi", "Faqat mini-oʼyin vaqtida oʼynash mumkin. Start vaqti - bosh ekranda."],
      team_call_limit: ["Jamoa allaqachon chaqirildi", "Jamoani kuniga bir marta chaqirish mumkin."],
      team_call_unavailable: ["Hozir chaqirib boʼlmaydi", "Jamoani bugungi mini-oʼyin tugagunicha chaqirish mumkin."],
      story_sharing_disabled: ["Stories hozircha mavjud emas", "Funksiya kampaniya qayta boshlangach yoqiladi."],
      story_not_available: ["Karta hali tayyor emas", "Bu Stories uchun faol yig'ilish, jamoa yoki tasdiqlangan natija kerak."],
      story_not_found: ["Stories topilmadi", "Yangi karta yarating va qayta urinib ko'ring."],
      story_code_expired: ["Karta muddati tugagan", "Yangi Stories kartasini yarating."],
    },
    toastOffer: ["Qoidalar qabul qilindi", "Endi cheklar, jamoalar va mini-oʻyinlar mavjud."],
    infoPrizes: ["Sovrinlar", "1-turda 4 ta ikki kishilik sayohat oʼynaladi. Sovrinning toʼliq shartlari Aksiya qoidalarida koʼrsatilgan. Yangiliklarni kuzatib boring - sovrin jamgʼarmasi oshirilishi va yangi sovrinlar bilan toʼldirilishi mumkin."],
    infoOffer: ["Aksiya qoidalari", "Shartlarni qabul qilishdan oldin Aksiya qoidalarining oʼzbek tilidagi toʼliq matnini ochib, tanishib chiqing."],
    offerAccept: "Qoidalarni qabul qilish",
    offerOpen: "Toʼliq matnni ochish",
    offerUnavailable: "Oʼzbek tilidagi tasdiqlangan Aksiya qoidalari hozircha mavjud emas. Shartlarni qabul qilish oʼchirilgan.",
    infoTeamX2: [
      "x2 qanday ishlaydi",
      `Ikki barobar faqat oʼsha kungi xaridlarga amal qiladi. Jamoadan ${PLAYERS_UZ}ni bir kunga toʼplang: har biri mini-oʼyinda oʼynaydi va oʼsha kunning oʼzida cheklarini skanlaydi - shunda oʼsha cheklar imkoniyatlari ikki barobar boʼladi. Boshqa kunlarning cheklari va oʼynamaganlarning imkoniyatlari ikki barobar boʼlmaydi.`,
    ],
    infoSupport: ["Yordam", `Ishonch telefoni: +998 71 215-82-85. Muammo takrorlansa, ${BOT_USERNAME} Telegram-botiga yozing.`],
    loading: "STING POYGASI yuklanmoqda",
    campaignGateTitle: "STING POYGASI hozircha yopiq",
    campaignGateBody: "Cheklar 2026-yil 31-iyul kuni Toshkent vaqti bilan soat 12:00 da avtomatik qabul qilina boshlaydi.",
    campaignEndedTitle: "STING POYGASI yakunlandi",
    campaignEndedBody: "Ishtirokingiz uchun rahmat! Sting yangiliklarini kuzatib boring.",
    callYes: "Jamoa chaqirildi", callNo: "Chaqiradigan kishi yoʻq",
    callDelivered: (s, t) => `Yetkazildi: ${s}/${t}.`, callNone: `Jamoada hozircha ${BOT_USERNAME} botini ochgan ishtirokchilar yoʻq.`,
    practiceDone: ["Mashq tugadi", `Mashq natijasi saqlanmaydi. Haqiqiy oʼyin ${BOT_USERNAME} botiga taklif yetkazilganidan soʼng kun oxirigacha ochiq boʼladi.`],
    teamUpd: "Jamoa yangilandi", teamNow: (n) => `Endi siz ${n} jamoasidasiz.`,
    leftTitle: "Siz jamoadan chiqdingiz", leftBody: "Shaxsiy imkoniyatlaringiz sizda qoldi.",
    profileTitle: "Profil saqlandi", profileBody: "Ism va jamoa yangilandi.",
  },
};
const tr = (lang) => (lang === "uz" ? MSG.uz : MSG.ru);

function telegramWebApp() {
  return window.Telegram && window.Telegram.WebApp ? window.Telegram.WebApp : null;
}

const INIT_DATA_KEY = "sting_tg_init_data";

// Initialize the official Telegram SDK once. init() + restoreInitData() is what
// the working NCP Meals app does; it reliably recovers the launch params (raw
// initData) for valid signed launch types where legacy telegram-web-app.js leaves
// window.Telegram.WebApp.initData empty (warm/reused WebView, direct/menu/inline
// launches). Reply-keyboard WebApp launches are not fixable here: Telegram
// returns empty WebAppInitData for them by design. Idempotent and safe outside
// Telegram (wrapped).
let tgSdkReady = false;
function ensureTgSdk() {
  if (tgSdkReady) return;
  tgSdkReady = true;
  try {
    tgSdkInit();
    tgRestoreInitData();
  } catch {}
}

function sdkInitData() {
  ensureTgSdk();
  try {
    return tgRetrieveRawInitData() || "";
  } catch {
    return "";
  }
}

function rawInitDataFromHash() {
  const hash = (window.location.hash || "").replace(/^#/, "");
  return hash ? new URLSearchParams(hash).get("tgWebAppData") || "" : "";
}

// Resolve raw Telegram initData from every source it can hide in: the official
// SDK (retrieveRawInitData, robust across launch types), then legacy
// window.Telegram.WebApp.initData, then the launch hash, then a sessionStorage
// cache of the first non-empty value (survives warm WebView reloads).
function telegramInitData() {
  const tg = telegramWebApp();
  const live =
    sdkInitData() ||
    (tg && typeof tg.initData === "string" && tg.initData) ||
    rawInitDataFromHash();
  if (live) {
    try {
      sessionStorage.setItem(INIT_DATA_KEY, live);
    } catch {}
    return live;
  }
  try {
    return sessionStorage.getItem(INIT_DATA_KEY) || "";
  } catch {
    return "";
  }
}

const DEV_USER_KEY = "sting_dev_user";

// Dev-only: ?devUser=<telegram_id> pins the acting user when the app runs in a
// plain browser against a backend started with STING_DEV_AUTH_BYPASS=1. Ignored
// whenever real Telegram initData is present, and the backend refuses the
// bypass in production, so this is inert on the live app.
function devUserId() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = (params.get("devUser") || params.get("dev_user") || "").trim();
  if (fromUrl) {
    try {
      sessionStorage.setItem(DEV_USER_KEY, fromUrl);
    } catch {}
    return fromUrl;
  }
  try {
    return sessionStorage.getItem(DEV_USER_KEY) || "";
  } catch {
    return "";
  }
}

/* ---------- session token (SF-SEC-001) ---------- */

// initData travels on every request in the legacy scheme, which makes replay
// windows as long as the launch. We trade it once for a short-lived session
// token and send Bearer afterwards. The backend still accepts the legacy header
// during the transition, so any failure here degrades instead of blocking.
const SESSION_KEY = "sf1.session";
// Do not reuse a token that expires while the next request is in flight.
const SESSION_SKEW_MS = 60_000;

let session = null; // { token, expiresAt (unix seconds), initData }
let sessionHydrated = false;
let sessionPromise = null;
let sessionRestartRequired = false;

function sessionUsable(candidate) {
  return !!(
    candidate &&
    typeof candidate.token === "string" &&
    candidate.token &&
    Number(candidate.expiresAt) * 1000 > Date.now() + SESSION_SKEW_MS
  );
}

// A reload inside the same Telegram launch cannot re-exchange: the backend has
// already burned that initData and answers 403 replay_detected. So the stored
// token is reused only when it was minted from the very initData this page
// still has; a fresh launch brings fresh initData and forces a new exchange.
function restoreSession() {
  if (sessionHydrated) return;
  sessionHydrated = true;
  let raw = "";
  try {
    raw = sessionStorage.getItem(SESSION_KEY) || "";
  } catch {}
  if (!raw) return;
  try {
    const saved = JSON.parse(raw);
    if (saved && saved.initData && saved.initData === telegramInitData() && sessionUsable(saved)) {
      session = saved;
    }
  } catch {}
}

function clearSession() {
  session = null;
  sessionHydrated = true;
  try {
    sessionStorage.removeItem(SESSION_KEY);
  } catch {}
}

function sessionToken() {
  restoreSession();
  return sessionUsable(session) ? session.token : "";
}

function sessionNeedsRestart() {
  return sessionRestartRequired;
}

async function fetchJsonWithTimeout(input, options = {}, timeoutMs = API_REQUEST_TIMEOUT_MS) {
  const controller = new AbortController();
  const callerSignal = options.signal;
  const abort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) abort();
    else callerSignal.addEventListener("abort", abort, { once: true });
  }
  const timer = window.setTimeout(abort, timeoutMs);
  try {
    const response = await fetch(input, { ...options, signal: controller.signal });
    let body = null;
    let parseError = null;
    try {
      body = await response.json();
    } catch (error) {
      if (error?.name === "AbortError") throw error;
      parseError = error;
    }
    return { response, body, parseError };
  } finally {
    window.clearTimeout(timer);
    callerSignal?.removeEventListener("abort", abort);
  }
}

async function exchangeSession(initData) {
  let response;
  let body = null;
  try {
    const result = await fetchJsonWithTimeout(
      API_BASE + "/api/auth/session",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Telegram-Init-Data": initData },
      },
      SESSION_REQUEST_TIMEOUT_MS,
    );
    response = result.response;
    body = result.body;
  } catch {
    return ""; // offline / 5xx-ish: legacy initData headers keep the app usable
  }
  const error = (body && (body.error || (body.detail && body.detail.error))) || "";
  if (response.status === 403 && error === "replay_detected") {
    sessionRestartRequired = true;
    return "";
  }
  if (!response.ok || !body || !body.token || !body.expiresAt) return "";
  session = { token: body.token, expiresAt: body.expiresAt, initData };
  try {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {}
  return session.token;
}

// One exchange per load: concurrent callers share the in-flight promise instead
// of racing two exchanges (the second would be rejected as a replay).
function ensureSession() {
  const token = sessionToken();
  if (token) return Promise.resolve(token);
  // Once this launch has been rejected as a replay, every further exchange with
  // the same initData is rejected too - stop hammering the endpoint.
  if (sessionRestartRequired) return Promise.resolve("");
  if (sessionPromise) return sessionPromise;
  const initData = telegramInitData();
  if (!initData) return Promise.resolve(""); // dev bypass / non-Telegram: nothing to exchange
  sessionPromise = exchangeSession(initData).finally(() => {
    sessionPromise = null;
  });
  return sessionPromise;
}

function authHeaders() {
  const token = sessionToken();
  if (token) {
    return { Authorization: `Bearer ${token}` };
  }
  const initData = telegramInitData();
  if (initData) {
    return { "X-Telegram-Init-Data": initData };
  }
  const devUser = devUserId();
  if (devUser) {
    return { "X-Dev-Telegram-Id": devUser };
  }
  return {};
}

// Analytics deliberately bypasses api(): event retries must never replay a
// business request or turn an event outage into a user-visible mutation error.
async function waitForEventSession() {
  let timer;
  try {
    await Promise.race([
      ensureSession(),
      new Promise((_, reject) => {
        timer = setTimeout(() => reject(new EventBatchError(0, "Event session timeout")), EVENT_REQUEST_TIMEOUT_MS);
      }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function postEventBatch(events) {
  try {
    return await fetchJsonWithTimeout(
      API_BASE + "/api/events/batch",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ events }),
      },
      EVENT_REQUEST_TIMEOUT_MS,
    );
  } catch (error) {
    // Preserve a status attached by a transport wrapper. Plain fetch/network
    // failures have no HTTP response and intentionally remain status 0.
    const status = Number.isInteger(error?.status)
      ? error.status
      : Number.isInteger(error?.response?.status)
        ? error.response.status
        : 0;
    if (error instanceof EventBatchError) throw error;
    throw new EventBatchError(status, "Event batch network failure");
  }
}

const eventOutbox = createEventOutbox({
  sender: async (events) => {
    // Reuse the existing session/initData exchange without routing through
    // api(), whose retry path is reserved for business requests.
    await waitForEventSession();
    let batch = await postEventBatch(events);
    if (batch.response.status === 401) {
      clearSession();
      await waitForEventSession();
      batch = await postEventBatch(events);
    }
    if (!batch.response.ok) throw new EventBatchError(batch.response.status);
    if (batch.parseError) throw new EventBatchError(502, "Event batch response was not JSON");
    return batch.body;
  },
});

function recordUiEvent(eventName, result, details = {}) {
  // Queueing is intentionally fire-and-forget.  The durable outbox owns
  // retries and caps; event capture never blocks a business interaction.
  void eventOutbox.enqueue({ eventName, result, ...details }).catch(() => {});
}

function recordUiGesture(actionId, screen, payload = {}) {
  recordUiEvent(CLIENT_EVENT_NAME.GESTURE_COMPLETED, EVENT_RESULT.SUCCEEDED, {
    screen,
    payload: { actionId, ...payload },
  });
}

function telegramStartParam() {
  const tg = telegramWebApp();
  return tg && tg.initDataUnsafe ? tg.initDataUnsafe.start_param || "" : "";
}

// Public rules links opened from the landing carry a `site` token naming the
// landing page language. The token is never a URL: it can only select one of
// these fixed pages, so the viewer cannot be used as an open redirect. Any
// other value keeps the historical bot deep link for direct links.
const LANDING_RETURN_URL_BY_SITE = {
  ru: "https://stingpromo.com/ru",
  uz: "https://stingpromo.com/uz",
  en: "https://stingpromo.com/eng",
};
const PUBLIC_OFFER_FALLBACK_URL = "https://t.me/stingf1bot";

function publicOfferReturnUrl(params) {
  const site = params.get("site");
  if (site && Object.prototype.hasOwnProperty.call(LANDING_RETURN_URL_BY_SITE, site)) {
    return LANDING_RETURN_URL_BY_SITE[site];
  }
  return PUBLIC_OFFER_FALLBACK_URL;
}

function requestedView() {
  const params = new URLSearchParams(window.location.search);
  const raw =
    params.get("view") ||
    params.get("screen") ||
    params.get("section") ||
    params.get("startapp") ||
    telegramStartParam() ||
    "home";
  if (raw.startsWith("team_")) return "team";
  return VIEW_ALIASES[raw.toLowerCase()] || "home";
}

function requestedAction() {
  const params = new URLSearchParams(window.location.search);
  const action = params.get("action");
  const source = params.get("source");
  return {
    action: ["scan", "play", "invite"].includes(action) ? action : null,
    source: ["daily_push", "team_call", "menu"].includes(source) ? source : "menu",
  };
}

function initialRoute() {
  const view = requestedView();
  const intent = requestedAction();
  return {
    screen: view === "scan" || view === "offer" ? "home" : view,
    scanning: view === "scan",
    offerRequested: view === "offer",
    ...intent,
  };
}

async function api(path, options = {}, retriedAfterAuth = false) {
  const headers = {
    "Content-Type": "application/json",
    ...authHeaders(),
    ...(options.headers || {}),
  };
  const usedBearer = typeof headers.Authorization === "string";
  const request = await fetchJsonWithTimeout(
    API_BASE + path,
    {
      ...options,
      headers,
    },
    API_REQUEST_TIMEOUT_MS,
  );
  const { response, body: responseBody, parseError } = request;
  if (!response.ok) {
    let detail = { code: "request_failed" };
    try {
      if (parseError) throw parseError;
      const json = responseBody;
      const body = json.detail || json; // backend AppError -> {error, message}; legacy -> {detail:{code}}
      if (typeof body === "string") {
        detail = { code: "request_failed", message: body }; // HTTPException {"detail": "text"}
      } else if (Array.isArray(body)) {
        detail = { code: "validation_error", message: body.map((e) => e.msg).join("; ") }; // pydantic errors
      } else {
        detail = { ...body, code: body.code || body.error || "request_failed", message: body.message };
      }
    } catch {}
    // An expired session token is recoverable exactly once: drop it, exchange a
    // new one and replay the request. If the exchange comes back as a replay,
    // this launch can no longer authenticate - only a relaunch can.
    if (response.status === 401 && usedBearer && !retriedAfterAuth && detail.code === "auth_expired") {
      clearSession();
      await ensureSession();
      if (sessionNeedsRestart()) throw { code: "auth_restart_required" };
      return api(path, options, true);
    }
    throw detail;
  }
  if (parseError) throw parseError;
  return responseBody;
}

function loadOfferContent(language) {
  return api(`/api/offer/current?lang=${encodeURIComponent(language)}`);
}

// Multipart upload. Deliberately NOT api(): that helper pins
// `Content-Type: application/json`, and a multipart body needs the boundary the
// browser generates. XHR is used instead of fetch because it is the only way to
// show real upload progress on a slow Uzbek mobile connection, where a 20 MB
// passport scan can take minutes and a silent screen looks broken.
const UPLOAD_TIMEOUT_MS = 180_000;

function apiUpload(path, formData, onProgress) {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", API_BASE + path);
    request.timeout = UPLOAD_TIMEOUT_MS;
    const headers = authHeaders();
    Object.keys(headers).forEach((name) => request.setRequestHeader(name, headers[name]));
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && typeof onProgress === "function") {
        onProgress(event.total > 0 ? event.loaded / event.total : 0);
      }
    };
    const fail = () => reject({ code: "request_failed" });
    request.onerror = fail;
    request.ontimeout = fail;
    request.onabort = fail;
    request.onload = () => {
      let body = null;
      try {
        body = JSON.parse(request.responseText || "null");
      } catch {
        body = null;
      }
      if (request.status >= 200 && request.status < 300) {
        if (body === null) {
          fail();
          return;
        }
        resolve(body);
        return;
      }
      const detail = (body && body.detail) || body || {};
      reject({
        ...detail,
        code: detail.code || detail.error || "request_failed",
        message: detail.message,
      });
    };
    request.send(formData);
  });
}

function loadPrizeDossier() {
  return api("/api/prize/dossier");
}

function submitPrizeDossier(formData, onProgress) {
  return apiUpload("/api/prize/dossier", formData, onProgress);
}

async function noOfferAcceptance() {}

async function waitForTelegramInitData() {
  if (!telegramWebApp() || telegramInitData()) return;
  for (let attempt = 0; attempt < 10; attempt += 1) {
    await new Promise((resolve) => window.setTimeout(resolve, 100));
    if (telegramInitData()) return;
  }
}

function initialLoadNotice(error, retry, lang) {
  const m = tr(lang);
  if (error && error.code === "auth_invalid") {
    return { actionId: "notice:auth_invalid", title: m.loadAuthTitle, body: m.loadAuthBody, retry, visual: "auth" };
  }
  if (error && error.code === "auth_restart_required") {
    const [title, body] = m.e.auth_restart_required;
    return { actionId: "notice:auth_restart_required", title, body, retry, visual: "auth" };
  }
  return { actionId: "notice:initial_load_failed", title: m.loadPauseTitle, body: m.loadPauseBody, retry };
}

function App() {
  const firstRoute = React.useMemo(initialRoute, []);
  const [authed, setAuthed] = React.useState(true);
  const [lang, setLang] = React.useState("ru");
  const [screen, setScreen] = React.useState(firstRoute.screen);
  const [scanning, setScanning] = React.useState(firstRoute.scanning);
  const [scanBusy, setScanBusy] = React.useState(false);
  // Bumped when a scan starts and when the scanner closes; a stale in-flight scan
  // (e.g. mid not_found-retry) checks it before touching state so it can't update
  // the UI after the user has left the scanner.
  const scanTokenRef = React.useRef(0);
  const [reward, setReward] = React.useState(null);
  const [pit, setPit] = React.useState(false);
  const [pitSessionId, setPitSessionId] = React.useState(null);
  const [pitChallenge, setPitChallenge] = React.useState(null);
  const [results, setResults] = React.useState(null);
  const [state, setState] = React.useState(null);
  const [toast, setToast] = React.useState(null);
  const [teamManage, setTeamManage] = React.useState(false);
  const [profileSheet, setProfileSheet] = React.useState(false);
  const [inviteModal, setInviteModal] = React.useState(null);
  const [storyPreview, setStoryPreview] = React.useState(null);
  const [contentModal, setContentModal] = React.useState(null);
  const [offerViewer, setOfferViewer] = React.useState(firstRoute.offerRequested);
  const [notice, setNotice] = React.useState(null);
  const [tutorial, setTutorial] = React.useState(false);
  const [gameIntro, setGameIntro] = React.useState(false);
  const [playReady, setPlayReady] = React.useState(false);
  const [practice, setPractice] = React.useState(false);
  const [bakuFocusRound, setBakuFocusRound] = React.useState(15);
  const [pendingAction, setPendingAction] = React.useState(null);
  const [ticketHistory, setTicketHistory] = React.useState(null);
  const [ticketHistoryLoading, setTicketHistoryLoading] = React.useState(false);
  const pendingRef = React.useRef(false);
  const loadingMoreRef = React.useRef(false);
  const screenRef = React.useRef(screen);
  const scanningRef = React.useRef(scanning);
  const resumedIntentRef = React.useRef(false);
  const visibleRefreshFrameRef = React.useRef(0);
  const viewedScreenRef = React.useRef("");
  const trackedSheetsRef = React.useRef({});
  const loadRequestGate = React.useMemo(() => createLatestRequestGate(), []);

  // Toasts auto-dismiss; replacing a toast restarts the timer. The leaving
  // phase drops [data-on] so the CSS exit transition plays before unmount.
  const [toastLeaving, setToastLeaving] = React.useState(false);
  React.useEffect(() => {
    if (!toast) return undefined;
    setToastLeaving(false);
    const leave = window.setTimeout(() => setToastLeaving(true), 4600); // 400ms window fits the rubber exit
    const timer = window.setTimeout(() => setToast(null), 5000);
    return () => {
      window.clearTimeout(leave);
      window.clearTimeout(timer);
    };
  }, [toast]);

  // Refetch state exactly at the window boundaries so canPlay/results flip on
  // time (one shot per boundary; the server clock offset corrects client skew).
  React.useEffect(() => {
    const win = state?.pitStop?.window;
    if (!win) return undefined;
    const offset = Date.parse(state.pitStop.serverNow || "") - Date.now() || 0;
    const timers = [win.startsAt, win.endsAt]
      .map((iso) => Date.parse(iso) - (Date.now() + offset) + 500)
      .filter((delay) => delay > 0 && delay < 26 * 3600 * 1000)
      .map((delay) => window.setTimeout(load, delay));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [state?.pitStop?.window?.startsAt, state?.pitStop?.window?.endsAt]);

  React.useEffect(() => {
    ensureTgSdk();
    const tg = window.Telegram && window.Telegram.WebApp;
    tg && tg.ready && tg.ready();
    tg && tg.expand && tg.expand();
    tg && tg.setHeaderColor && tg.setHeaderColor("#050505");
    tg && tg.setBackgroundColor && tg.setBackgroundColor("#050505");
    // Audio unlock must live inside a gesture (WebView autoplay policy). The
    // first tap unlocks and plays the branded sonic; later taps only re-resume
    // the context after iOS suspends it.
    let first = true;
    const onPointerDown = () => {
      Snd.unlock();
      J.globalTap(); // near-invisible bottom glow: the screen acknowledges every touch
      if (first) {
        first = false;
        Snd.playBrand();
      }
    };
    window.addEventListener("pointerdown", onPointerDown);
    load();
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, []);

  // Telegram/iOS may suspend intervals and boundary timeouts while the Mini App
  // is in the background. Refresh server-owned window state as soon as the
  // WebView becomes visible again; the countdown hook catches up independently.
  React.useEffect(() => {
    const refreshVisibleState = () => {
      if (document.visibilityState !== "visible" || visibleRefreshFrameRef.current) return;
      visibleRefreshFrameRef.current = window.requestAnimationFrame(() => {
        visibleRefreshFrameRef.current = 0;
        void load();
      });
    };
    document.addEventListener("visibilitychange", refreshVisibleState);
    window.addEventListener("pageshow", refreshVisibleState);
    return () => {
      if (visibleRefreshFrameRef.current) {
        window.cancelAnimationFrame(visibleRefreshFrameRef.current);
        visibleRefreshFrameRef.current = 0;
      }
      document.removeEventListener("visibilitychange", refreshVisibleState);
      window.removeEventListener("pageshow", refreshVisibleState);
    };
  }, []);

  React.useEffect(() => {
    screenRef.current = screen;
  }, [screen]);

  React.useEffect(() => {
    scanningRef.current = scanning;
  }, [scanning]);

  // Install the event client before the first state request.  App-open evidence
  // is useful even when auth/state loading later fails.
  React.useEffect(() => {
    const stopOutbox = eventOutbox.start();
    const stopCapture = installEventCapture({
      record: recordUiEvent,
      getScreen: () => (scanningRef.current ? "scan" : screenRef.current),
    });
    recordUiEvent(CLIENT_EVENT_NAME.APP_OPENED, EVENT_RESULT.STARTED, {
      screen: firstRoute.scanning ? "scan" : firstRoute.screen,
    });
    const onVisibility = () =>
      recordUiEvent(CLIENT_EVENT_NAME.APP_VISIBILITY_CHANGED, EVENT_RESULT.RECEIVED, {
        screen: scanningRef.current ? "scan" : screenRef.current,
        payload: { state: document.visibilityState },
      });
    const onPageHide = () =>
      recordUiEvent(CLIENT_EVENT_NAME.APP_CLOSED, EVENT_RESULT.RECEIVED, {
        screen: scanningRef.current ? "scan" : screenRef.current,
      });
    const onNetwork = () =>
      recordUiEvent(CLIENT_EVENT_NAME.NETWORK_CHANGED, EVENT_RESULT.RECEIVED, {
        screen: scanningRef.current ? "scan" : screenRef.current,
        payload: { online: navigator.onLine },
      });
    const onError = (event) =>
      recordUiEvent(CLIENT_EVENT_NAME.CLIENT_ERROR, EVENT_RESULT.FAILED, {
        screen: scanningRef.current ? "scan" : screenRef.current,
        payload: { kind: "error", message: String(event.message || "unknown") },
      });
    const onUnhandledRejection = (event) =>
      recordUiEvent(CLIENT_EVENT_NAME.CLIENT_ERROR, EVENT_RESULT.FAILED, {
        screen: scanningRef.current ? "scan" : screenRef.current,
        payload: {
          kind: "unhandledrejection",
          message: String(event.reason instanceof Error ? event.reason.message : event.reason || "unknown"),
        },
      });
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("online", onNetwork);
    window.addEventListener("offline", onNetwork);
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onUnhandledRejection);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("online", onNetwork);
      window.removeEventListener("offline", onNetwork);
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      stopCapture();
      stopOutbox();
    };
  }, []);

  React.useEffect(() => {
    const viewedScreen = scanning ? "scan" : screen;
    if (viewedScreenRef.current === viewedScreen) return;
    viewedScreenRef.current = viewedScreen;
    recordUiEvent(CLIENT_EVENT_NAME.SCREEN_VIEWED, EVENT_RESULT.STARTED, { screen: viewedScreen });
  }, [screen, scanning]);

  React.useEffect(() => {
    if (!notice) return;
    recordUiEvent(CLIENT_EVENT_NAME.ERROR_SHOWN, EVENT_RESULT.FAILED, {
      screen: scanning ? "scan" : screen,
      payload: { actionId: "error_notice" },
    });
  }, [notice]);

  React.useEffect(() => {
    const sheets = {
      scan: scanning,
      team_manage: teamManage,
      profile: profileSheet,
      invite: Boolean(inviteModal),
      story_preview: Boolean(storyPreview),
      content: Boolean(contentModal),
      offer: offerViewer,
      tutorial,
      game_intro: gameIntro,
      play_ready: playReady,
      results: Boolean(results),
      reward: Boolean(reward),
    };
    Object.entries(sheets).forEach(([sheet, open]) => {
      const previous = trackedSheetsRef.current[sheet];
      if (previous === undefined) {
        trackedSheetsRef.current[sheet] = open;
        if (!open) return;
      } else if (previous === open) {
        return;
      } else {
        trackedSheetsRef.current[sheet] = open;
      }
      recordUiEvent(
        open ? CLIENT_EVENT_NAME.SHEET_OPENED : CLIENT_EVENT_NAME.SHEET_CLOSED,
        open ? EVENT_RESULT.STARTED : EVENT_RESULT.SUCCEEDED,
        {
          screen: scanning ? "scan" : screen,
          payload: { actionId: `sheet:${sheet}` },
        },
      );
    });
  }, [scanning, teamManage, profileSheet, inviteModal, storyPreview, contentModal, offerViewer, tutorial, gameIntro, playReady, results, reward, screen]);

  React.useEffect(() => {
    if (!state || screen !== "tickets") {
      setTicketHistory(null);
      return undefined;
    }
    let live = true;
    setTicketHistoryLoading(true);
    api(`/api/history/tickets?limit=${TICKET_HISTORY_LIMIT}&offset=0`)
      .then((page) => {
        if (live) setTicketHistory(Array.isArray(page.items) ? page : null);
      })
      .catch(() => {
        if (live) setTicketHistory(null);
      })
      .finally(() => {
        if (live) setTicketHistoryLoading(false);
      });
    return () => {
      live = false;
    };
  }, [screen, state?.user?.stateVersion]);

  async function load() {
    const requestId = loadRequestGate.begin();
    try {
      await waitForTelegramInitData();
      if (!loadRequestGate.isLatest(requestId)) return;
      // A public `?view=offer` link must render the legal source without an
      // authenticated state request. Inside Telegram, initData is present and
      // the same route proceeds to the real acceptance gate.
      if (firstRoute.offerRequested && !telegramInitData()) return;
      // Trade initData for a session token before the first authenticated call
      // so /api/state already travels with Bearer. Deduplicated and cached, so
      // later load() calls (retry, window boundaries) cost nothing; a failed
      // exchange falls through to the legacy initData header.
      await ensureSession();
      if (!loadRequestGate.isLatest(requestId)) return;
      if (sessionNeedsRestart()) throw { code: "auth_restart_required" };
      // Always call the backend so every launch attempt is recorded (a failed
      // auth is logged as auth.telegram_rejected). Do not suppress it client-side.
      let next = await api("/api/state");
      if (!loadRequestGate.isLatest(requestId)) return;
      if (
        firstRoute.action &&
        (next.user?.phone == null || next.user?.offerAccepted !== true) &&
        next.user?.pendingAction?.action !== firstRoute.action
      ) {
        const saved = await api("/api/profile/pending-action", {
          method: "POST",
          body: JSON.stringify({ action: firstRoute.action, source: firstRoute.source }),
        });
        next = saved.state;
      }
      if (!loadRequestGate.isLatest(requestId)) return;
      setNotice(null);
      setState(next);
      setLang(next.user.language || "ru");
      setPitSessionId(next.pitStop.activeSessionId || null);
      // New participants see the zero-byte tutorial before legal acceptance
      // or contact collection. It is keyed to server-owned offer state rather
      // than a shared WebView localStorage flag, so a previous account cannot
      // skip it. Deep links (scan/team/...) prove intent - do not interrupt.
      if (
        next.campaign?.available === true &&
        next.user?.offerAccepted !== true &&
        (next.receipts || []).length === 0 &&
        (next.tickets || []).length === 0 &&
        firstRoute.screen === "home" &&
        !firstRoute.scanning &&
        !firstRoute.offerRequested &&
        !firstRoute.action
      ) {
        setTutorial(true);
      }
    } catch (error) {
      if (!loadRequestGate.isLatest(requestId)) return;
      const note = initialLoadNotice(error, load, lang);
      // Diagnostic: if auth failed inside Telegram, log which initData source was
      // empty so we can pinpoint the cause. Console only - never shown to the user.
      if (error && error.code === "auth_invalid" && telegramWebApp() && !telegramInitData()) {
        const tgd = telegramWebApp();
        let ssLen = -1;
        try {
          ssLen = (sessionStorage.getItem(INIT_DATA_KEY) || "").length;
        } catch {}
        const diag =
          `id=${((tgd && tgd.initData) || "").length}` +
          ` unsafe=${tgd && tgd.initDataUnsafe ? Object.keys(tgd.initDataUnsafe).length : "none"}` +
          ` user=${tgd && tgd.initDataUnsafe && tgd.initDataUnsafe.user ? "yes" : "no"}` +
          ` hash=${(window.location.hash || "").length}` +
          ` ss=${ssLen}` +
          ` plat=${(tgd && tgd.platform) || "?"}` +
          ` ver=${(tgd && tgd.version) || "?"}`;
        console.warn("auth_invalid diag:", diag);
      }
      setNotice(note);
    }
  }

  function errorCopy(detail) {
    const code = detail.code || "error";
    const actionCode = String(code).toLowerCase().replace(/[^a-z0-9_-]+/g, "_").slice(0, 64) || "error";
    const m = tr(lang);
    if (code === "tour_not_active" && detail.campaignStatus === "ended") {
      return { actionId: "notice:tour_ended", title: m.campaignEndedTitle, body: m.campaignEndedBody };
    }
    const [title, body] = m.e[code] || [code, detail.message || m.errFallback];
    return { actionId: `notice:${actionCode}`, title, body };
  }

  async function run(action, success) {
    if (pendingAction) return;
    if (pendingRef.current) return;
    pendingRef.current = true;
    setPendingAction("mutation");
    setToast(null);
    setNotice(null);
    try {
      const result = await action();
      success && success(result);
    } catch (error) {
      const detail = typeof error === "object" && error ? error : { code: "error" };
      const copy = errorCopy(detail);
      setNotice(copy);
    } finally {
      pendingRef.current = false;
      setPendingAction(null);
    }
  }

  function openInvite() {
    run(
      () => api("/api/team/invite", { method: "POST", body: "{}" }),
      (result) => {
        setState(result.state);
        setInviteModal(result.invite);
        Hx.notify("success");
      },
    );
  }

  function startGame() {
    setPlayReady(false);
    run(
      () => api("/api/pitstop/start", { method: "POST" }),
      (result) => {
        setState(result.state);
        setPitSessionId(result.sessionId);
        setPitChallenge(result.challenge);
        setPit(true);
      },
    );
  }

  function buildGameSessionCapture(status, detail = {}, activeSessionId = null, isPractice = false) {
    const sessionId = isPractice ? null : activeSessionId || detail.sessionId || detail.challengeId || null;
    return {
      screen: "pit",
      entityType: isPractice ? "pitstop_practice" : "pitstop_session",
      entityId: sessionId || undefined,
      payload: {
        mode: isPractice ? "practice" : "campaign",
        status,
        endedReason: detail.endedReason || (status === "finished" ? "completed" : "user_exit"),
        challengeId: isPractice ? null : detail.challengeId || null,
        sessionId,
        trace: Array.isArray(detail.events)
          ? detail.events.filter((event) => event?.type !== "wild_tap")
          : [],
        tapTimestampsMs: Array.isArray(detail.tapTimestampsMs) ? detail.tapTimestampsMs : [],
      },
    };
  }

  function recordGameSession(status, detail = {}, isPractice = false) {
    recordUiEvent(
      CLIENT_EVENT_NAME.GAME_SESSION,
      status === "finished" ? EVENT_RESULT.SUCCEEDED : EVENT_RESULT.REJECTED,
      buildGameSessionCapture(status, detail, pitSessionId, isPractice),
    );
  }

  async function loadMoreTicketHistory() {
    if (loadingMoreRef.current || !ticketHistory?.hasMore) return;
    loadingMoreRef.current = true;
    const offset = Array.isArray(ticketHistory.items) ? ticketHistory.items.length : 0;
    setTicketHistoryLoading(true);
    try {
      const page = await api(`/api/history/tickets?limit=${TICKET_HISTORY_LIMIT}&offset=${offset}`);
      if (screenRef.current !== "tickets") return;
      setTicketHistory((current) => {
        if (screenRef.current !== "tickets" || current === null) return current;
        const previous = Array.isArray(current?.items) ? current.items : [];
        const nextItems = Array.isArray(page.items) ? page.items : [];
        return { ...page, items: [...previous, ...nextItems] };
      });
    } catch {
      setNotice(errorCopy({ code: "request_failed" }));
    } finally {
      loadingMoreRef.current = false;
      setTicketHistoryLoading(false);
    }
  }

  // Receipt scan: client-side "is this a Soliq QR" pre-check, spinner while the
  // backend verifies with Soliq, and a short auto-retry when Soliq answers
  // "receipt still forming" (a just-purchased receipt may not be synced yet).
  async function scanReceipt(qrUrl) {
    setToast(null);
    setNotice(null);
    const value = (qrUrl || "").trim();
    if (!/^https?:\/\/ofd\.soliq\.uz\//i.test(value)) {
      setNotice(errorCopy({ code: "receipt_invalid_qr" }));
      return;
    }
    const maxRetries = 2;
    const token = (scanTokenRef.current += 1);
    const alive = () => scanTokenRef.current === token;
    setScanBusy(true);
    try {
      for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        try {
          const result = await api("/api/receipts/scan", {
            method: "POST",
            body: JSON.stringify({ qrUrl: value }),
          });
          if (!alive()) return; // scanner closed while the request was in flight
          setScanning(false);
          setState(result.state);
          setReward(result);
          return;
        } catch (error) {
          if (!alive()) return;
          const aborted = error && error.name === "AbortError";
          const detail = aborted ? { code: "receipt_soliq_unavailable" } : typeof error === "object" && error ? error : { code: "error" };
          if (detail.code === "receipt_not_found" && attempt < maxRetries) {
            await new Promise((resolve) => window.setTimeout(resolve, 2000));
            if (!alive()) return; // user left the scanner during the retry wait
            continue; // keep the spinner up; Soliq may have synced by now
          }
          setNotice(errorCopy(detail));
          return;
        }
      }
    } finally {
      if (alive()) setScanBusy(false);
    }
  }

  async function acceptOffer(offer) {
    const tg = telegramWebApp();
    const result = await api("/api/profile/offer/accept", {
      method: "POST",
      body: JSON.stringify({
        tourId: offer.tourId,
        offerVersion: offer.version,
        language: offer.language,
        contentSha256: offer.contentSha256,
        platform: tg?.platform || null,
      }),
    });
    setState(result.state);
    setOfferViewer(false);
    setToast({ title: tr(lang).toastOffer[0], body: tr(lang).toastOffer[1] });
  }

  function openInfo(kind) {
    const m = tr(lang);
    const content = {
      prizes: { actionId: "info:prizes", title: m.infoPrizes[0], body: m.infoPrizes[1] },
      offer: null,
      support: { actionId: "info:support", title: m.infoSupport[0], body: m.infoSupport[1] },
      teamX2: { actionId: "info:team_x2", title: m.infoTeamX2[0], body: m.infoTeamX2[1] },
    }[kind];
    if (kind === "offer") {
      setOfferViewer(true);
    } else {
      setContentModal(content);
    }
  }

  React.useEffect(() => {
    if (!state?.user?.phone || state?.user?.offerAccepted !== true || resumedIntentRef.current) return;
    const intent = firstRoute.action || state.user.pendingAction?.action;
    if (!intent) return;
    resumedIntentRef.current = true;
    void api("/api/profile/pending-action", {
      method: "POST",
      body: JSON.stringify({ action: null, source: null }),
    }).catch(() => {});
    const url = new URL(window.location.href);
    url.searchParams.delete("action");
    url.searchParams.delete("source");
    window.history.replaceState({}, "", url);
    if (intent === "scan") {
      setScreen("home");
      setScanning(true);
    } else if (intent === "play") {
      setScreen("home");
      setPlayReady(true);
    } else if (intent === "invite") {
      openInvite();
    }
  }, [state?.user?.phone, state?.user?.offerAccepted, state?.user?.pendingAction?.action]);

  if (!state && firstRoute.offerRequested) {
    const publicParams = new URLSearchParams(window.location.search);
    const requestedLanguage = publicParams.get("lang");
    const publicLanguage = ["ru", "uz", "en"].includes(requestedLanguage) ? requestedLanguage : "ru";
    const returnUrl = publicOfferReturnUrl(publicParams);
    return (
      <OfferGate
        language={publicLanguage}
        readOnly
        loadOffer={loadOfferContent}
        onAccept={noOfferAcceptance}
        onClose={() => window.location.assign(returnUrl)}
        onGesture={(actionId, payload) => recordUiGesture(actionId, "offer", payload)}
      />
    );
  }

  if (!state) {
    return (
      <>
        <div style={{ flex: 1, display: "grid", placeItems: "center", color: "#fff", fontFamily: "var(--font-heading)" }}>
          {tr(lang).loading}
        </div>
        {notice && <InfoModal content={notice} onClose={() => setNotice(null)} lang={lang} />}
        {toast && <Toast toast={toast} leaving={toastLeaving} />}
      </>
    );
  }

  if (!authed) {
    return (
      <>
        <S.LoginScreen lang={lang} onLogin={() => setAuthed(true)} />
      </>
    );
  }

  const offerContents = state?.currentTour?.offerContents || {};
  const offerLanguage = offerContents[lang] ? lang : offerContents.ru ? "ru" : lang;
  const offerDescriptor = offerContents[offerLanguage];

  const toggleLanguage = () => {
    const next = lang === "ru" ? "uz" : "ru";
    setLang(next);
    recordUiEvent(CLIENT_EVENT_NAME.LANGUAGE_CHANGED, EVENT_RESULT.SUCCEEDED, {
      screen: scanning ? "scan" : screen,
      payload: { actionId: `language:${next}` },
    });
  };

  if (tutorial) {
    return (
      <TutorialOverlay
        lang={lang}
        finalTarget="offer"
        allowSkip={false}
        onGesture={(actionId, payload) => recordUiGesture(actionId, "tutorial", payload)}
        onDone={(target) => {
          markTutorialSeen();
          setTutorial(false);
          if (target === "offer") setOfferViewer(true);
        }}
      />
    );
  }

  if (offerViewer && offerDescriptor) {
    return (
      <OfferGate
        language={offerLanguage}
        readOnly={state?.user?.offerAccepted === true || offerDescriptor.status !== "approved"}
        loadOffer={loadOfferContent}
        onAccept={acceptOffer}
        onClose={() => {
          setOfferViewer(false);
          setScreen("home");
        }}
        onGesture={(actionId, payload) => recordUiGesture(actionId, "offer", payload)}
      />
    );
  }

  if (state?.user?.offerAccepted !== true && offerDescriptor?.status === "approved") {
    return (
      <OfferGate
        language={offerLanguage}
        loadOffer={loadOfferContent}
        onAccept={acceptOffer}
        onClose={closeMiniApp}
        onGesture={(actionId, payload) => recordUiGesture(actionId, "offer", payload)}
      />
    );
  }

  if (!state?.user?.phone) {
    return <RegistrationGate lang={lang} />;
  }

  // The prize paperwork happens AFTER the campaign closes: a winner of the 22.08
  // or 29.08 draw must still reach the document form once the tour has ended.
  // Documented exception to the campaign gate, guarded by
  // tests/campaign-gate-contract.mjs. Only a registered winner passes; everyone
  // else keeps the normal closed-campaign screen.
  if (screen === "prize" && state?.prize?.isWinner === true) {
    return (
      <PrizeScreen
        lang={lang}
        loadDossier={loadPrizeDossier}
        submitDossier={submitPrizeDossier}
        onHome={() => setScreen("home")}
      />
    );
  }

  // A scheduled campaign stays browseable for client approval. The backend
  // remains the authority for receipt, game, team and other value mutations.
  const campaignStatus = state?.campaign?.status;
  const campaignIsScheduled = campaignStatus === "scheduled";
  if (state?.campaign?.available !== true && !campaignIsScheduled) {
    const ended = campaignStatus === "ended";
    return (
      <>
        <CampaignUnavailable
          body={ended ? tr(lang).campaignEndedBody : tr(lang).campaignGateBody}
          language={lang}
          onLanguage={toggleLanguage}
          title={ended ? tr(lang).campaignEndedTitle : tr(lang).campaignGateTitle}
        />
        {/* The closed-campaign screen is exactly where the winners of the 22.08
          * and 29.08 draws land. Without this banner the Telegram deep link is
          * their only way into the paperwork, and a lost message is a lost prize. */}
        {state?.prize?.isWinner === true ? <PrizeBanner lang={lang} onOpen={() => setScreen("prize")} /> : null}
        {notice && <InfoModal content={notice} onClose={() => setNotice(null)} lang={lang} />}
      </>
    );
  }

  if (state?.user?.offerAccepted !== true) {
    return (
      <CampaignUnavailable
        body={tr(lang).offerUnavailable}
        language={lang}
        onLanguage={toggleLanguage}
        title={tr(lang).infoOffer[0]}
      />
    );
  }

  const nav = (id) => {
    if (id === "scan") {
      setScanning(true);
      return;
    }
    if (id === "__lang") {
      toggleLanguage();
      return;
    }
    setScanning(false);
    setScreen(id);
  };

  const user = state.user || {};
  const team = state.team || null;
  const storySharing = Boolean(state?.config?.features?.storySharing);
  const tickets = Array.isArray(state.tickets) ? state.tickets : [];
  const receipts = Array.isArray(state.receipts) ? state.receipts : [];
  const ticketCount = state.ticketCount ?? tickets.length;
  const receiptCount = state.receiptCount ?? receipts.length;
  const pitStop = state.pitStop || {};
  const screenState = { ...state, user, team, tickets, receipts, ticketCount, receiptCount, pitStop };

  const callTeam = () =>
    run(
      () => api("/api/team/call", { method: "POST", body: "{}" }),
      (result) => {
        setState(result.state);
        const call = result.call || {};
        const m = tr(lang);
        setToast({
          title: call.targetCount ? m.callYes : m.callNo,
          body: call.targetCount ? m.callDelivered(call.sentCount, call.targetCount) : m.callNone,
        });
      },
    );

  const prepareStory = (kind) =>
    run(
      () => api("/api/stories", { method:"POST", body:JSON.stringify({ kind, language:lang }) }),
      (story) => setStoryPreview(story),
    );

  const canShareStory = (() => {
    const tg = telegramWebApp();
    if (!tg || typeof tg.shareToStory !== "function") return false;
    return typeof tg.isVersionAtLeast !== "function" || tg.isVersionAtLeast("7.8");
  })();

  const shareStory = () => {
    if (!storyPreview) return;
    const tg = telegramWebApp();
    if (!canShareStory || !tg) {
      window.open(storyPreview.mediaUrl, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      tg.shareToStory(storyPreview.mediaUrl, {
        text: storyPreview.caption,
        widget_link: storyPreview.widgetLink,
      });
      void api(`/api/stories/${storyPreview.id}/editor-opened`, { method:"POST", body:"{}" }).catch(() => {});
    } catch {
      window.open(storyPreview.mediaUrl, "_blank", "noopener,noreferrer");
    }
  };

  let body = null;
  if (scanning) {
    body = (
      <S.ScanScreen
        lang={lang}
        disabled={pendingAction !== null}
        onClose={() => {
          scanTokenRef.current += 1; // invalidate any in-flight scan so it can't update state
          setScanBusy(false);
          setScanning(false);
        }}
        onReward={scanReceipt}
        onCameraPermissionRequested={() =>
          recordUiEvent(CLIENT_EVENT_NAME.CAMERA_PERMISSION_REQUESTED, EVENT_RESULT.STARTED, {
            screen: "scan",
            payload: { actionId: "scan:camera" },
          })
        }
        onCameraPermissionResult={(result) =>
          recordUiEvent(
            CLIENT_EVENT_NAME.CAMERA_PERMISSION_RESULT,
            result === "granted" ? EVENT_RESULT.ACCEPTED : EVENT_RESULT.REJECTED,
            {
              screen: "scan",
              payload: { actionId: `scan:camera:${result}` },
            },
          )
        }
        onQrDetected={(source, value) =>
          recordUiEvent(CLIENT_EVENT_NAME.QR_DETECTED, EVENT_RESULT.ACCEPTED, {
            screen: "scan",
            payload: { source, value },
          })
        }
      />
    );
  } else if (screen === "home") {
    body = (
      <S.HomeScreen
        lang={lang}
        user={user}
        team={team}
        points={user.points || 0}
        showPoints={state?.currentTour?.mechanics?.showPoints ?? true}
        tickets={ticketCount}
        pitStop={pitStop}
        onCall={callTeam}
        disabled={pendingAction !== null}
        onNav={nav}
        onBakuFocus={(round) => {
          setBakuFocusRound(round || 15);
          nav("baku");
        }}
        onPractice={() => setGameIntro(true)}
        storySharing={storySharing}
        onStoryRally={() => prepareStory("team_rally")}
        onPlay={() => setPlayReady(true)}
      />
    );
  } else if (screen === "team") {
    body = (
      <S.TeamScreen
        lang={lang}
        state={screenState}
        onChangeTeam={() => setTeamManage(true)}
        onInvite={openInvite}
        onCall={callTeam}
        storySharing={storySharing}
        onStoryRally={() => prepareStory("team_rally")}
        onCreateTeam={() => setTeamManage(true)}
        onInfo={openInfo}
        disabled={pendingAction !== null}
      />
    );
  } else if (screen === "baku") {
    body = <S.BakuScreen lang={lang} state={screenState} focusRound={bakuFocusRound} onRules={() => setOfferViewer(true)} />;
  } else if (screen === "cabinet") {
    body = (
      <S.CabinetScreen
        lang={lang}
        points={user.points || 0}
        state={screenState}
        onNav={nav}
        onEditProfile={() => setProfileSheet(true)}
        onTutorial={() => setTutorial(true)}
        onLogout={() => {
          setAuthed(false);
          setScreen("home");
        }}
        onInfo={openInfo}
      />
    );
  } else if (screen === "tickets") {
    const ticketState = ticketHistory ? { ...screenState, tickets: ticketHistory.items, ticketCount: ticketHistory.totalCount, ticketsHasMore: ticketHistory.hasMore } : screenState;
    body = <S.TicketsScreen lang={lang} state={ticketState} onLoadMore={loadMoreTicketHistory} loadingMore={ticketHistoryLoading} onBack={(target) => (target === "scan" ? setScanning(true) : setScreen("home"))} />;
  } else if (screen === "rules") {
    body = <S.RulesScreen lang={lang} onBack={() => setScreen("home")} onNav={nav} onMiniGame={() => setGameIntro(true)} />;
  } else if (screen === "prize") {
    // Reached with an open campaign; the closed-campaign path returns earlier.
    body = <PrizeScreen lang={lang} loadDossier={loadPrizeDossier} submitDossier={submitPrizeDossier} onHome={() => setScreen("home")} />;
  }

  return (
    <>
      {body}
      {screen === "home" && state?.prize?.isWinner === true ? <PrizeBanner lang={lang} onOpen={() => nav("prize")} /> : null}
      {!scanning && screen !== "prize" && <S.BottomNav active={screen} onNav={nav} lang={lang} />}
      {pit && (
        <S.PitStopGame
          lang={lang}
          challenge={practice ? null : pitChallenge}
          onExit={(trace) => {
            recordGameSession("aborted", trace || {}, practice);
            setPit(false);
            setPractice(false);
            setPitSessionId(null);
            setPitChallenge(null);
          }}
          onSessionComplete={(detail) => {
            recordGameSession("finished", detail || {}, practice);
          }}
          onFinish={(time, det) => {
            setPit(false);
            if (practice) {
              // Practice run: no session, no points - just explain how the real game works.
              setPractice(false);
              setToast({ title: tr(lang).practiceDone[0], body: tr(lang).practiceDone[1] });
              return;
            }
            run(
              () =>
                api("/api/pitstop/finish", {
                  method: "POST",
                  body: JSON.stringify({
                    sessionId: pitSessionId,
                    challengeId: det.challengeId,
                    events: det.events,
                  }),
                }),
              (result) => {
                setState(result.state);
                setPitSessionId(null);
                setPitChallenge(null);
                setResults({ time, hits: det.hits, pen: det.pen, backend: result });
              },
            );
          }}
        />
      )}
      {gameIntro && (
        <S.GameIntroModal
          lang={lang}
          onClose={() => setGameIntro(false)}
          onPractice={() => {
            setGameIntro(false);
            setPractice(true);
            setPit(true);
          }}
        />
      )}
      {playReady && (
        <S.PlayReadyModal
          lang={lang}
          state={screenState}
          onClose={() => setPlayReady(false)}
          onPlay={startGame}
          onInvite={openInvite}
          onScan={() => {
            setPlayReady(false);
            setScanning(true);
          }}
          disabled={pendingAction !== null}
        />
      )}
      {results && (
        <S.ResultsScreen
          lang={lang}
          result={results}
          state={screenState}
          storySharing={storySharing}
          onStory={() => prepareStory("personal_result")}
          onInvite={openInvite}
          onClose={() => setResults(null)}
        />
      )}
      {reward && (
        <S.RewardOverlay
          lang={lang}
          reward={reward}
          state={screenState}
          onPlay={() => {
            setReward(null);
            setPlayReady(true);
          }}
          onInvite={openInvite}
          onClose={() => setReward(null)}
        />
      )}
      {scanBusy && <ScanBusyOverlay lang={lang} />}
      {teamManage && (
        <S.TeamManageModal
          lang={lang}
          state={screenState}
          onClose={() => setTeamManage(false)}
          onCreate={(name) => {
            recordUiEvent(CLIENT_EVENT_NAME.TEAM_SAVED, EVENT_RESULT.STARTED, {
              screen: "team",
              payload: { teamName: name },
            });
            return run(
              () =>
                api(team ? "/api/team/change" : "/api/team/create", {
                  method: "POST",
                  body: JSON.stringify(team ? { new_team_name: name } : { team_name: name }),
                }),
              (result) => {
                setState(result.state);
                setTeamManage(false);
                Hx.notify("success");
                setToast({ title: tr(lang).teamUpd, body: tr(lang).teamNow(result.state.team?.name || name) });
              },
            );
          }}
          disabled={pendingAction !== null}
          onLeave={() =>
            run(
              () => api("/api/team/leave", { method: "POST", body: JSON.stringify({}) }),
              (result) => {
                setState(result.state);
                setTeamManage(false);
                setToast({ title: tr(lang).leftTitle, body: tr(lang).leftBody });
              },
            )
          }
        />
      )}
      {profileSheet && (
        <S.ProfileSheet
          lang={lang}
          state={screenState}
          onClose={() => setProfileSheet(false)}
          onSave={(displayName, teamName) => {
            recordUiEvent(CLIENT_EVENT_NAME.PROFILE_SAVED, EVENT_RESULT.STARTED, {
              screen: "cabinet",
              payload: { displayName, teamName },
            });
            return run(
              () => api("/api/profile", { method: "POST", body: JSON.stringify({ display_name: displayName, team_name: teamName, language: lang }) }),
              (result) => {
                setState(result.state);
                setProfileSheet(false);
                setToast({ title: tr(lang).profileTitle, body: tr(lang).profileBody });
              },
            );
          }}
          disabled={pendingAction !== null}
        />
      )}
      {inviteModal && <S.InviteModal invite={inviteModal} storySharing={storySharing} onStory={() => prepareStory("team_invite")} onClose={() => setInviteModal(null)} lang={lang} />}
      {storyPreview && <S.StoryPreviewModal story={storyPreview} canShare={canShareStory} onShare={shareStory} onGesture={(actionId, payload) => recordUiGesture(actionId, "story_preview", payload)} onClose={() => setStoryPreview(null)} lang={lang} />}
      {contentModal && <InfoModal content={contentModal} onClose={() => setContentModal(null)} lang={lang} />}
      {notice && <InfoModal content={notice} onClose={() => setNotice(null)} lang={lang} />}
      {toast && <Toast toast={toast} leaving={toastLeaving} />}
    </>
  );
}

function RegistrationGate({ lang }) {
  const ru = lang !== "uz";
  const openBot = () => {
    const url = `https://t.me/${BOT_USERNAME.replace("@", "")}?start=continue`;
    const tg = telegramWebApp();
    if (tg?.openTelegramLink) tg.openTelegramLink(url);
    else window.location.href = url;
  };
  return (
    <div style={{ minHeight:"100%", display:"grid", placeItems:"center", padding:24, background:"var(--surface-base)", color:"#fff" }}>
      <div style={{ width:"100%", maxWidth:420, background:"var(--surface-card)", border:"1px solid var(--border-subtle)", borderRadius:"var(--r-xl)", padding:24 }}>
        <div style={{ fontFamily:"var(--font-display)", fontWeight:900, fontSize:30, lineHeight:1, textTransform:"uppercase" }}>
          {ru ? "Подтвердите телефон" : "Telefonni tasdiqlang"}
        </div>
        <p style={{ fontFamily:"var(--font-body)", color:"var(--text-muted)", fontSize:15, lineHeight:1.5, margin:"14px 0 20px" }}>
          {ru
            ? "Правила акции приняты. Вернитесь в бот и отправьте свой номер официальной кнопкой Telegram. Сканирование, игра и приглашения откроются автоматически."
            : "Aksiya qoidalari qabul qilindi. Botga qayting va Telegramning rasmiy tugmasi orqali oʼz telefon raqamingizni yuboring. Skanerlash, oʼyin va takliflar avtomatik ochiladi."}
        </p>
        <button data-ui-action="auth:open_bot" onClick={openBot} className="tilepress" style={{ width:"100%", height:58, border:0, borderRadius:"var(--r-lg)", background:"var(--grad-red)", color:"#fff", fontFamily:"var(--font-heading)", fontSize:16, textTransform:"uppercase" }}>
          {ru ? "Перейти в бот" : "Botga oʼtish"}
        </button>
      </div>
    </div>
  );
}

function ScanBusyOverlay({ lang }) {
  const [line1, line2] = tr(lang).scanReading;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 60,
        display: "grid",
        placeItems: "center",
        background: "rgba(6,4,10,0.82)",
        backdropFilter: "blur(2px)",
      }}
      role="status"
      aria-live="polite"
    >
      <style>{"@keyframes stingSpin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 18 }}>
        <div
          style={{
            width: 54,
            height: 54,
            borderRadius: "50%",
            border: "4px solid rgba(255,255,255,0.18)",
            borderTopColor: "var(--sting-gold, #f5c542)",
            animation: "stingSpin 0.9s linear infinite",
          }}
        />
        <div style={{ textAlign: "center", color: "#fff", fontFamily: "var(--font-heading)" }}>
          <div style={{ fontSize: 18 }}>{line1}</div>
          <div style={{ fontSize: 13, opacity: 0.7, marginTop: 4 }}>{line2}</div>
        </div>
      </div>
    </div>
  );
}

function Toast({ toast, leaving }) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    const raf = window.requestAnimationFrame(() => setMounted(true));
    return () => window.cancelAnimationFrame(raf);
  }, []);
  return (
    <div className="app-toast" data-on={mounted && !leaving ? "1" : "0"} data-leave={leaving ? "1" : "0"}>
      <strong>{toast.title}</strong>
      <span>{toast.body}</span>
    </div>
  );
}

function InfoModal({ content, onClose, lang }) {
  const actionId = content.actionId || "info:unknown";
  const primaryKind = content.action ? "action" : content.retry ? "retry" : "acknowledge";
  return (
    <div className="modal-backdrop">
      <div className="modal-box">
        {content.visual === "auth" && (
          <img
            src={authNoticeVisual(lang)}
            alt=""
            aria-hidden="true"
            loading="lazy"
            style={{
              width: "100%",
              height: 128,
              objectFit: "cover",
              objectPosition: "top",
              borderRadius: "var(--r-lg)",
              border: "1px solid var(--border-subtle)",
              marginBottom: 14,
              display: "block",
            }}
          />
        )}
        <h2>{content.title}</h2>
        <p>{withSting(content.body)}</p>
        {content.linkUrl && (
          <a
            data-ui-action={`${actionId}:link`}
            href={content.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ display: "block", margin: "0 0 14px", color: "var(--brand-gold, #f5c542)", fontWeight: 700 }}
          >
            {content.linkLabel}
          </a>
        )}
        <button
          data-ui-action={`${actionId}:${primaryKind}`}
          onClick={content.action || content.retry || onClose}
          style={{
            width: "100%",
            height: 54,
            border: "none",
            borderRadius: "var(--r-lg)",
            background: "var(--grad-red)",
            color: "#fff",
            fontFamily: "var(--font-heading)",
            textTransform: "uppercase",
          }}
        >
          {content.actionLabel || (content.retry ? tr(lang).retry : tr(lang).ok)}
        </button>
        {content.retry && (
          <button
            data-ui-action={`${actionId}:close`}
            onClick={onClose}
            style={{
              width: "100%",
              marginTop: 10,
              height: 44,
              border: "1px solid var(--border-subtle)",
              borderRadius: "var(--r-lg)",
              background: "transparent",
              color: "var(--text-muted)",
            }}
          >
            {tr(lang).close}
          </button>
        )}
      </div>
    </div>
  );
}

function CampaignEndedApp() {
  const tg = window.Telegram && window.Telegram.WebApp;
  const telegramLang = tg && tg.initDataUnsafe && tg.initDataUnsafe.user
    ? String(tg.initDataUnsafe.user.language_code || "")
    : "";
  const [lang, setLang] = React.useState(telegramLang.toLowerCase().startsWith("uz") ? "uz" : "ru");
  React.useEffect(() => {
    tg && tg.ready && tg.ready();
    tg && tg.expand && tg.expand();
  }, []);
  return (
    <CampaignUnavailable
      body={tr(lang).campaignEndedBody}
      language={lang}
      onLanguage={() => setLang((current) => (current === "uz" ? "ru" : "uz"))}
      title={tr(lang).campaignEndedTitle}
    />
  );
}

const root = document.getElementById("app");
if (!root) throw new Error("Mini App root element is missing");

createRoot(root).render(CAMPAIGN_ENDED ? <CampaignEndedApp /> : <App />);
